import { useEffect } from "react";
import { useLocation, matchPath } from "react-router-dom";

/**
 * Keeps document.title in sync with the current route.
 *
 * This is a client-only SPA (no SSR), so per-route titles are set here rather
 * than in index.html. The homepage title in index.html is the crawler/no-JS
 * fallback; once React mounts this component takes over.
 *
 * Patterns are matched exactly (end: true) against the pathname, so dynamic
 * routes like "/app/links/:id" don't collide with their list pages.
 */

const BRAND = "cliqo";
const DEFAULT_TITLE = `${BRAND} — Short links, kept simple`;

// Most specific patterns first is not required (exact match), but kept readable.
const ROUTE_TITLES: { pattern: string; title: string }[] = [
  { pattern: "/", title: DEFAULT_TITLE },
  { pattern: "/compare", title: `Compare · ${BRAND}` },
  { pattern: "/docs", title: `API docs · ${BRAND}` },
  { pattern: "/use-cases", title: `Use cases · ${BRAND}` },
  { pattern: "/privacy", title: `Privacy Policy · ${BRAND}` },
  { pattern: "/terms", title: `Terms of Service · ${BRAND}` },
  { pattern: "/refunds", title: `Refund Policy · ${BRAND}` },

  // Auth
  { pattern: "/login", title: `Sign in · ${BRAND}` },
  { pattern: "/register", title: `Create account · ${BRAND}` },
  { pattern: "/verify-email", title: `Verify email · ${BRAND}` },
  { pattern: "/forgot-password", title: `Forgot password · ${BRAND}` },
  { pattern: "/reset-password", title: `Reset password · ${BRAND}` },
  { pattern: "/ask-password", title: `Password required · ${BRAND}` },
  { pattern: "/oauth/callback", title: `Signing in… · ${BRAND}` },

  // App
  { pattern: "/app/dashboard", title: `Dashboard · ${BRAND}` },
  { pattern: "/app/links", title: `Links · ${BRAND}` },
  { pattern: "/app/links/:id", title: `Link details · ${BRAND}` },
  { pattern: "/app/tags", title: `Tags · ${BRAND}` },
  { pattern: "/app/domains", title: `Domains · ${BRAND}` },
  { pattern: "/app/api-keys", title: `API keys · ${BRAND}` },
  { pattern: "/app/billing", title: `Billing · ${BRAND}` },
  { pattern: "/app/profile", title: `Profile · ${BRAND}` },

  // Admin
  { pattern: "/admin/dashboard", title: `Admin · Dashboard · ${BRAND}` },
  { pattern: "/admin/users", title: `Admin · Users · ${BRAND}` },
  { pattern: "/admin/users/:id", title: `Admin · User details · ${BRAND}` },
  { pattern: "/admin/links", title: `Admin · Links · ${BRAND}` },
  { pattern: "/admin/payments", title: `Admin · Payments · ${BRAND}` },
  { pattern: "/admin/billing-plans", title: `Admin · Billing plans · ${BRAND}` },
  { pattern: "/admin/domains", title: `Admin · Domains · ${BRAND}` },
];

// Path prefixes whose pages manage their own <head> via the <Seo> component
// (title, description, canonical, JSON-LD). TitleManager stays out of their way
// so the two don't overwrite each other.
const SELF_MANAGED_PREFIXES = ["/news", "/compare", "/docs", "/use-cases"];

const TitleManager: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (SELF_MANAGED_PREFIXES.some((p) => pathname.startsWith(p))) return;

    const match = ROUTE_TITLES.find((r) =>
      matchPath({ path: r.pattern, end: true }, pathname),
    );
    document.title = match ? match.title : `Page not found · ${BRAND}`;
  }, [pathname]);

  return null;
};

export default TitleManager;